import { ofetch } from 'ofetch';
import { currentTenantSlug } from '@/services/tenant';
import { attachUiSessionId } from '@/services/ui-session';
import { useAuthStore } from '@/stores/auth-store';

/* ---------------------------------------------------------------------------------------------- */

// Define error type
export class ApiFetchError extends Error {
   constructor(
      public status: number,
      message: string,
      public data?: any,
   ) {
      super(message);
      this.name = 'ApiError';
   }
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * Creates a cancellable instance of ofetch with authentication and error handling
 */
export function createCancellableApiFetch() {
   const controller = new AbortController();

   const instance = ofetch.create({
      // base url config
      baseURL: import.meta.env.VITE_API_BASE_URL,
      signal: controller.signal,

      headers: {
         'Content-Type': 'application/json',
      },

      // request interceptor
      async onRequest({ options }) {
         const authStore = useAuthStore();
         await attachUiSessionId(options.headers, { signal: options.signal });

         if (authStore.accessToken) {
            options.headers.set('Authorization', `Bearer ${authStore.accessToken}`);
         }

         // ENG-359: scope the request to the active tenant derived from the host.
         options.headers.set('X-Tenant', currentTenantSlug());
      },
      async onResponseError({ response }) {
         throw new ApiFetchError(
            response.status,
            response._data?.detail || response.statusText,
            response._data,
         );
      },
   });

   return {
      fetch: instance,
      abort: () => controller.abort(),
   };
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * Creates an instance of ofetch for backend.apaluma.com
 */
export const useApiFetch = ofetch.create({
   baseURL: import.meta.env.VITE_API_BASE_URL,
   headers: {
      'Content-Type': 'application/json',
   },

   // retry options
   retry: 3,
   retryStatusCodes: [408, 429, 504, 500],
   retryDelay: 100,

   async onRequest({ options }) {
      const authStore = useAuthStore();

      // if (authStore.isTokenExpired) {
      //    await authStore.init();
      // }

      await authStore.init();

      if (authStore.accessToken) {
         options.headers.set('Authorization', `Bearer ${authStore.accessToken}`);
      }

      await attachUiSessionId(options.headers, {
         sessionInitialized: true,
         signal: options.signal,
      });

      // ENG-359: scope the request to the active tenant derived from the host.
      options.headers.set('X-Tenant', currentTenantSlug());

      // Remove the Content-Type header if we're sending FormData
      if (options.body instanceof FormData) {
         options.headers.delete('Content-Type');
      }
   },
   // async onResponseError(ctx) {
   //    throw new ApiFetchError(
   //       ctx.response.status,
   //       ctx.response._data?.detail || ctx.response.statusText,
   //       ctx.response._data,
   //    );
   //
   //    return ctx;
   // },
});

/* ---------------------------------------------------------------------------------------------- */

/**
 * Create an instance of ofetch for api.apaluma.com calls
 */

export const usePublicApiFetch = ofetch.create({
   baseURL: import.meta.env.VITE_PUBLIC_API_BASE_URL,
   headers: {
      'Content-Type': 'application/json',
   },
   async onRequest({ options }) {
      await attachUiSessionId(options.headers, { signal: options.signal });
   },
   onResponseError({ response }) {
      if (response?.status === 401 || response?.status === 403) {
         throw new Error('Unauthorized access');
      }
      throw new Error('Failed to update data source');
   },
});
